// One rAF loop, running only while the section is near the viewport. Each
// frame reads the section's position (the only layout read), damps progress
// toward it, and hands it to the overlay and the scene. No scroll listeners,
// no scroll-jacking: native scrolling (and iOS momentum) stays untouched.
import { clamp } from './lib/ease';

export interface FrameFn { (p: number, dp: number, dt: number, t: number): void }

export function createScrollDriver(section: HTMLElement, stage: HTMLElement, touch: boolean, onFrame: FrameFn) {
  let travel = 1;
  let p = 0;
  let snap = true;
  let raf = 0;
  let last = 0;
  let t = 0;
  const measure = () => { travel = Math.max(1, section.offsetHeight - stage.offsetHeight); };
  const ro = new ResizeObserver(measure);
  ro.observe(section);
  ro.observe(stage);
  measure();

  const rate = touch ? 16 : 10;
  const tick = (now: number) => {
    raf = requestAnimationFrame(tick);
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 1 / 60;
    last = now;
    t += dt;
    const target = clamp(-section.getBoundingClientRect().top / travel, -0.5, 1);
    const prev = p;
    if (snap) { p = target; snap = false; } // deep links and reloads land in place
    else {
      p += (target - p) * (1 - Math.exp(-dt * rate));
      if (Math.abs(target - p) < 1e-4) p = target;
    }
    onFrame(p, (p - prev) / dt, dt, t);
  };

  const io = new IntersectionObserver(([e]) => {
    if (e.isIntersecting && !raf) { snap = true; last = 0; raf = requestAnimationFrame(tick); }
    else if (!e.isIntersecting && raf) { cancelAnimationFrame(raf); raf = 0; }
  }, { rootMargin: '25% 0px' });
  io.observe(section);

  return {
    travel: () => travel,
    dispose() { io.disconnect(); ro.disconnect(); cancelAnimationFrame(raf); raf = 0; },
  };
}
